import React, {
    createContext,
    Dispatch,
    MutableRefObject,
    ReactNode,
    SetStateAction,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
    useState,
} from 'react';
import {useFavouriteHandler, useSaveToCartHandler} from 'hooks/Product/Product';
import {useRecentlyAddedActions} from 'hooks/RecentlyAdded';
import {MATERIAL_TYPE} from 'components/customer/Materials/helper/constants';
import {compareObjects, useQueryParams} from 'shared/helpers';
import {
    ConfirmationOption,
    useConfirmationDialog,
} from 'shared/components/ConfirmationDialog/ConfirmationDialog';
import {cloneDeep, merge} from 'lodash';
import {BreadCrumb, useBreadCrumb} from 'hooks/Product/useBreadCrumb';
import {Cabinet} from 'components/customer/Product/entity/Cabinet';
import {GenericObject} from 'components/customer/Product/entity';
import {Structure} from 'components/customer/Product/entity/Structure';
import {ProductDataStore} from 'components/customer/Preview3D/usePreview3DData';
import {MaterialOptions} from 'shared/types/materials';

type DataType = string | number | object | boolean;
type ValidationData = {[key: string]: DataType};
type InitialValues = {[key: string]: DataType};

type SetValidationDataActionType = {
    key?: string;
    type?: 'single' | 'multiple' | 'reset';
    value?: DataType;
    data?: ValidationData;
};

interface Sundry {
    id?: number;
    code?: string;
    regularPrice?: number;
    advanced?: boolean;
}

interface AdvancedAttribute {
    [key: string]: string | number;
}

interface AttributeIds {
    [key: string]: string | number;
}

interface ItemVariation {
    [key: string]: string | number;
    regularPrice?: number;
}

interface RoomAssistantContent {
    changed_data?: {
        items: {
            name: string;
            apply_to_room: boolean;
            is_saved: boolean;
            room_data: string;
            product_data: string;
            image: string;
        }[];
        category: string;
    }[];
}

export interface DraerRunnerInfoRef extends GenericObject {
    maxRunnerDepth?: string | number;
}

type ProductContextInterface = {
    search?: string;
    setSearch?: Dispatch<SetStateAction<string>>;
    AddToCart?: (
        sundry: Sundry,
        quantity: number,
        advancedAttributeValues?: AdvancedAttribute,
        attributeIdStore?: AttributeIds,
        itemVariation?: ItemVariation,
        jobSundryId?: number | string
    ) => Promise<void>;
    AddToFavourite?: (
        item: {id?: number},
        isNew: boolean,
        isProduct?: boolean
    ) => Promise<void>;
    validationData?: ValidationData;
    addValidationData?: (key: string, value: DataType) => void;
    setValidationData?: Dispatch<SetValidationDataActionType>;
    getValidationData?: (key: string) => DataType;
    productDataStore?: ProductDataStore;
    setProduct?: (product: Cabinet) => void;
    getProductInfo?: (key: string) => string | number | boolean;
    setInitialValuesRef?: (initialValues: InitialValues) => void;
    getInitialValue?: (key: string) => DataType;
    setMaterialOptions?: (
        data: MaterialOptions | ((data: MaterialOptions) => MaterialOptions)
    ) => void;
    getMaterialOptions?: () => MaterialOptions;
    setPositionType?: (key: string, value: string) => void;
    shelvesPositionType?: {[key: string]: string};
    setNewShelfValue?: (name: string, value: boolean | number) => void;
    resetNewShelf?: () => void;
    newShelfStore?: MutableRefObject<{
        [key: string]: number | boolean;
    }>;
    shelvesHeightReference?: MutableRefObject<object>;
    setHeightReference?: (key: string, value: DataType) => void;
    shelvesHeightUpdated?: object;
    drawerRunnerInfoReference?: MutableRefObject<DraerRunnerInfoRef>;
    forty5Fields?: MutableRefObject<{
        widthChanged: boolean;
    }>;
    simpleShelvesReference?: MutableRefObject<number>;
    addRecentItem?: (itemId: number, type: number | string) => void;
    addInitialValue?: (key: string, value: DataType) => void;
    productHasDoor?: MutableRefObject<{
        [key: string]: boolean;
    }>;
    shelvesUpdatedReference?: MutableRefObject<GenericObject>;
    showDialog?: (options: ConfirmationOption) => void;
    hideDialog?: () => void;
    currentTab?: number;
    setCurrentTab?: Dispatch<SetStateAction<number>>;
    formStructure?: Structure[];
    setFormStructure?: Dispatch<SetStateAction<Structure[]>>;
    validateField?: MutableRefObject<boolean>;
    breadcrumbs?: BreadCrumb[];
    addBreadCrumb?: (item: BreadCrumb[]) => void;
    removeBreadCrumb?: (removeKey: string) => void;
    popBreadCrumb?: (index?: number) => void;
    resetBreadCrumb?: (items?: BreadCrumb[]) => void;
    doorPreviewOpacity?: number;
    setDoorPreviewOpacity?: Dispatch<SetStateAction<number>>;
    defaultValuesReference?: MutableRefObject<object>;
    isLoading?: boolean;
    startLoading?: () => void;
    finishLoading?: () => void;
    fieldIndex?: number;
    materialLoading?: boolean;
    setMaterialLoading?: Dispatch<SetStateAction<boolean>>;
    getAvailableDoors?: () => number[];
    getAvailableDrawers?: () => number[];
    materialFace?: boolean;
    setMaterialFace?: Dispatch<SetStateAction<boolean>>;
    roomAssistantContent?: RoomAssistantContent;
    setRoomAssistantContent?: Dispatch<SetStateAction<RoomAssistantContent>>;
    adjustableLegsQuantity?: number;
    setAdjustableLegsQuantity?: Dispatch<SetStateAction<number>>;
    isMiniBrowser?: boolean;
    setIsMiniBrowser?: Dispatch<SetStateAction<boolean>>;
    productDefaultsReference?: object;
};

export const ProductContext = createContext<ProductContextInterface>({});

export const useProductContext = () => useContext(ProductContext);

interface ProductProviderProps {
    children: ReactNode;
    index?: number;
    miniBrowser?: boolean;
}

export const ProductProvider = ({
    children,
    index = -1,
    miniBrowser = false,
}: ProductProviderProps) => {
    const {
        breadcrumbs,
        addBreadCrumb,
        removeBreadCrumb,
        popBreadCrumb,
        resetBreadCrumb,
    } = useBreadCrumb();
    const {dialog, showDialog, hideDialog} = useConfirmationDialog();
    const {AddToFavourite} = useFavouriteHandler();
    const {AddToCart} = useSaveToCartHandler();
    const {addRecentItem} = useRecentlyAddedActions();
    const {product} = useQueryParams() as {product: string};

    const productDataStore = useRef<{
        cabinet?: {
            attributes?: Cabinet;
        };
    }>({});
    const productReference = useRef<Cabinet>({});
    const initialValuesReference = useRef<InitialValues>({});
    const defaultValuesReference = useRef({});
    const productDefaultsReference = useRef({});
    const materialOptions = useRef({});
    const drawerRunnerInfoReference = useRef({});
    const shelvesHeightReference = useRef({});
    const simpleShelvesReference = useRef<number>();
    const shelvesUpdatedReference = useRef<GenericObject>({});
    const newShelfStore = useRef<{[key: string]: boolean | number}>({
        autoPosition: true,
        position: 0,
    });
    const forty5Fields = useRef({widthChanged: false});
    const productHasDoor = useRef({
        [MATERIAL_TYPE.EXTERIOR_COLOUR]: false,
        [MATERIAL_TYPE.CARCASE_COLOUR]: false,
    });
    const validateField = useRef(false);
    const fieldIndex = useRef(index).current;

    const [isMiniBrowser, setIsMiniBrowser] = useState(miniBrowser);
    const [search, setSearch] = useState('');
    const [shelvesHeightUpdated, setShelvesHeightUpdated] = useState({});
    const [currentTab, setCurrentTab] = useState(0);
    const [formStructure, setFormStructure] = useState([]);
    const [doorPreviewOpacity, setDoorPreviewOpacity] = useState(0.5);
    const [materialLoading, setMaterialLoading] = useState(false);
    const [materialFace, setMaterialFace] = useState(false);
    const [roomAssistantContent, setRoomAssistantContent] =
        useState<RoomAssistantContent>({});
    const [adjustableLegsQuantity, setAdjustableLegsQuantity] =
        useState<number>(4);

    const getAvailableDrawers = () => {
        if (productDataStore.current) {
            if (
                productDataStore?.current?.cabinet?.attributes
                    ?.available_drawers.length
            ) {
                return productDataStore.current.cabinet.attributes.available_drawers
                    .split(',')
                    .map((drawer) => parseInt(drawer));
            }
        }

        return [];
    };

    const getAvailableDoors = () => {
        if (productDataStore.current) {
            if (
                productDataStore?.current?.cabinet?.attributes?.available_doors
                    .length
            ) {
                return productDataStore.current.cabinet.attributes.available_doors
                    .split(',')
                    .map((door) => parseInt(door));
            }
        }

        return [];
    };

    const [shelvesPositionType, setShelvesPositionType] = useReducer(
        (
            state: {[key: string]: string},
            action: {payload: {key: string; value: string}}
        ) => {
            const {key, value} = action.payload;

            const currentPositions = cloneDeep(state);

            currentPositions[String(key)] = value;

            return currentPositions;
        },
        {}
    );

    const [validationData, setValidationData] = useReducer(
        (state: ValidationData, action: SetValidationDataActionType) => {
            let newState = cloneDeep(state);

            switch (action.type) {
                case 'single':
                    newState[action.key] = action.value;
                    break;

                case 'multiple':
                    newState = merge(newState, action.data);
                    break;

                case 'reset':
                    newState = {};
                    break;
            }

            return newState;
        },
        {}
    );

    const [productLoaderCount, dispatchLoader] = useReducer(
        (state: {count: number}, action: {type: string}): {count: number} => {
            switch (action.type) {
                case 'add':
                    return {count: state.count + 1};

                case 'minus':
                    return {count: state.count > 0 ? state.count - 1 : 0};

                default:
                    throw new Error('undefined action type');
            }
        },
        {count: 0}
    );

    const isLoading = useMemo<boolean>(
        () => productLoaderCount.count > 0,
        [productLoaderCount]
    );

    const startLoading = () => dispatchLoader({type: 'add'});

    const finishLoading = () => dispatchLoader({type: 'minus'});

    const addValidationData = (key: string, value: DataType) => {
        setValidationData({type: 'single', key, value});
    };

    const getValidationData = (key: string) => {
        return validationData.hasOwnProperty(key)
            ? validationData[String(key)]
            : {};
    };

    const setProduct = (product: Cabinet) => {
        productReference.current = product;
    };

    const getProductInfo = (key: string) => {
        if (key == 'image') {
            return productReference.current['image'];
        }
        if (key == 'changedImage') {
            const image =
                productReference.current.hasOwnProperty(
                    'manufacturer_cabinet_image'
                ) &&
                productReference.current['manufacturer_cabinet_image'] != null
                    ? '/' +
                      productReference.current['manufacturer_cabinet_image']
                    : '';
            return image;
        }
        return productReference.current.hasOwnProperty(key)
            ? productReference.current[String(key)]
            : false;
    };
    const setInitialValuesRef = (initialValues: InitialValues) => {
        initialValuesReference.current = initialValues;
    };

    const addInitialValue = (key: string, value: DataType) => {
        initialValuesReference.current[String(key)] = value;
    };

    const getInitialValue = (key: string) => {
        return initialValuesReference.current.hasOwnProperty(key)
            ? initialValuesReference.current[String(key)]
            : key;
    };

    const setMaterialOptions = (
        options: object | ((options: object) => object)
    ) => {
        if (typeof options === 'function') {
            materialOptions.current = options(
                materialOptions.current
            ) as object;

            return;
        }

        materialOptions.current = {...materialOptions.current, ...options};
    };

    const getMaterialOptions = () => {
        return materialOptions.current;
    };

    const setNewShelfValue = (name: string, value: boolean | number) => {
        newShelfStore.current[String(name)] = value;
    };

    const resetNewShelf = () => {
        newShelfStore.current = {
            autoPosition: true,
            position: 0,
        };
    };

    const setHeightReference = (key: string, value: DataType) => {
        const old = {
            ...shelvesHeightReference.current,
        };

        shelvesHeightReference.current = {
            ...old,
            [key]: value,
        };

        if (!compareObjects(old, shelvesHeightReference.current)) {
            setShelvesHeightUpdated({});
        }
    };

    const setPositionType = (key: string, value: string) => {
        setShelvesPositionType({payload: {key, value}});
    };

    useEffect(() => {
        setValidationData({type: 'reset'});
        setDoorPreviewOpacity(0.5);
        setFormStructure([]);
        materialOptions.current = {
            cabinet_ext_colour: {thickness: 16.5},
            cabinet_carc_colour: {thickness: 16.5},
        };
        validateField.current = false;
        productDataStore.current = {};
        productDefaultsReference.current = {};
    }, [product]);

    return (
        <ProductContext.Provider
            value={{
                search,
                setSearch,
                AddToCart,
                AddToFavourite,
                validationData,
                addValidationData,
                setValidationData,
                getValidationData,
                productDataStore,
                setProduct,
                getProductInfo,
                setInitialValuesRef,
                getInitialValue,
                setMaterialOptions,
                getMaterialOptions,
                setPositionType,
                shelvesPositionType,
                setNewShelfValue,
                resetNewShelf,
                newShelfStore,
                shelvesHeightReference,
                setHeightReference,
                shelvesHeightUpdated,
                drawerRunnerInfoReference,
                forty5Fields,
                simpleShelvesReference,
                addRecentItem,
                addInitialValue,
                productHasDoor,
                shelvesUpdatedReference,
                showDialog,
                hideDialog,
                currentTab,
                setCurrentTab,
                formStructure,
                setFormStructure,
                validateField,
                breadcrumbs,
                addBreadCrumb,
                removeBreadCrumb,
                popBreadCrumb,
                resetBreadCrumb,
                doorPreviewOpacity,
                setDoorPreviewOpacity,
                defaultValuesReference,
                productDefaultsReference,
                isLoading,
                startLoading,
                finishLoading,
                fieldIndex,
                materialLoading,
                setMaterialLoading,
                getAvailableDoors,
                getAvailableDrawers,
                materialFace,
                setMaterialFace,
                roomAssistantContent,
                setRoomAssistantContent,
                setAdjustableLegsQuantity,
                adjustableLegsQuantity,
                isMiniBrowser,
                setIsMiniBrowser,
            }}>
            {children}
            {dialog}
        </ProductContext.Provider>
    );
};
